import React, { useMemo, useCallback, useState } from "react";
import { Box, Button, Stack, Typography, alpha, Snackbar, Alert } from "@mui/material";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useTranslation } from "react-i18next";
import {
  extractAgendaMeetingLinks,
  meetingProviderLabel,
} from "../../utils/agendaMeetingLinks";
import { openAgentVideoJoinUrl } from "../../utils/activeVideoUi";

export default function AgendaMeetingLinks({
  location,
  description,
  meetingLinks,
  activeVideo = null,
}) {
  const { t } = useTranslation("teamAgenda");
  const { t: tTeam } = useTranslation("team");
  const [popupBlocked, setPopupBlocked] = useState(false);

  const links = useMemo(() => {
    const all = extractAgendaMeetingLinks({
      location,
      description,
      meetingLinks,
    });
    if (activeVideo?.isPrivate && !activeVideo?.canJoin) {
      return all.filter((link) => !/xpro/i.test(String(link.provider || "")));
    }
    return all;
  }, [location, description, meetingLinks, activeVideo]);

  const handleJoinLive = useCallback(() => {
    const url = activeVideo?.agentJoinUrl || activeVideo?.joinUrl;
    if (!url) return;
    openAgentVideoJoinUrl(url, {
      context: "agenda",
      onBlocked: () => setPopupBlocked(true),
    });
  }, [activeVideo]);

  const handleJoinStored = useCallback(
    (event, url) => {
      if (!url) return;
      event.preventDefault();
      openAgentVideoJoinUrl(url, {
        context: "agenda",
        onBlocked: () => setPopupBlocked(true),
      });
    },
    []
  );

  const showLiveJoin = Boolean(activeVideo?.canJoin && (activeVideo?.agentJoinUrl || activeVideo?.joinUrl));
  const showPrivateCaption = Boolean(activeVideo?.isPrivate && !activeVideo?.canJoin);

  if (!links.length && !showLiveJoin && !showPrivateCaption) return null;

  const single = links.length === 1 && !showLiveJoin;

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2,
        bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.12 : 0.06),
        border: "1px solid",
        borderColor: (theme) => alpha(theme.palette.primary.main, 0.25),
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: single || showLiveJoin || showPrivateCaption ? 1.25 : 1 }}>
        <VideocamOutlinedIcon fontSize="small" color="primary" />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={700} lineHeight={1.2}>
            {single || showLiveJoin ? t("meetingLinkTitle") : t("meetingLinksTitle")}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {t("meetingLinkHint")}
          </Typography>
        </Box>
      </Stack>

      {showLiveJoin ? (
        <Button
          variant="contained"
          color="error"
          size="medium"
          fullWidth
          startIcon={<VideocamOutlinedIcon />}
          onClick={handleJoinLive}
          sx={{
            justifyContent: "center",
            textTransform: "none",
            fontWeight: 700,
            py: 1,
            mb: links.length ? 1 : 0,
          }}
        >
          {t("activeCallJoin", "Chamada ativa — Entrar")}
        </Button>
      ) : null}

      {showPrivateCaption ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: links.length ? 1 : 0 }}>
          {tTeam("activeVideo.privateInProgress")}
        </Typography>
      ) : null}

      {links.length ? (
        <Stack direction={single ? "column" : "row"} spacing={1} flexWrap="wrap" useFlexGap>
          {links.map((link) => {
            const isXpro = /xpro/i.test(String(link.provider || ""));
            return (
            <Button
              key={link.url}
              variant="contained"
              color="primary"
              size={single ? "medium" : "small"}
              fullWidth={single}
              startIcon={<VideocamOutlinedIcon />}
              endIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={isXpro ? (event) => handleJoinStored(event, link.url) : undefined}
              sx={{
                justifyContent: single ? "center" : "flex-start",
                textTransform: "none",
                fontWeight: 700,
                py: single ? 1 : 0.75,
                flex: single ? undefined : "1 1 auto",
                minWidth: single ? undefined : 180,
              }}
            >
              {single
                ? t("joinMeetingSingle", { provider: meetingProviderLabel(link.provider, t) })
                : t("joinMeeting", { provider: meetingProviderLabel(link.provider, t) })}
            </Button>
            );
          })}
        </Stack>
      ) : null}
      <Snackbar
        open={popupBlocked}
        autoHideDuration={6000}
        onClose={() => setPopupBlocked(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="warning" onClose={() => setPopupBlocked(false)} variant="filled">
          {tTeam(
            "activeVideo.popupBlocked",
            "O navegador bloqueou a abertura da chamada. Permita pop-ups para este site e tente novamente."
          )}
        </Alert>
      </Snackbar>
    </Box>
  );
}
